import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock, Undo2, Unplug, Scale, BadgeCheck, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PublicLayout } from "@/components/PublicLayout";
import { Reveal } from "@/components/landing/Reveal";
import { HeroBackdrop } from "@/components/landing/HeroBackdrop";
import { Pipeline, FoundingPriceNote, ShapedTile } from "@/components/landing/Showcase";
import { FoundingBenefits } from "@/components/landing/CtaBits";
import { FOUNDING_FAQ as FAQ } from "@/seo/faq";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const STEPS = [
  ["Create your account", "Takes a minute. Free during the beta."],
  ["Connect your Etsy shop", "Official Etsy sign-in, no password shared."],
  ["Review your Health Report", "A score for every listing and a short \"Fix these first\" list."],
  ["Approve the fixes you like", "Old vs new, with a reason for each change. Skip anything you don't want."],
];

const TRUST = [
  [BadgeCheck, "Built on Etsy's official API"],
  [ShieldCheck, "Nothing changes without your approval"],
  [Lock, "Bestsellers locked by default"],
  [Undo2, "One-click undo"],
  [Unplug, "Disconnect anytime and your data is deleted"],
  [Scale, "No guaranteed rankings or sales"],
];

const RETURN = [
  ["20+", "Review at least 20 suggestions"],
  ["2", "Two short feedback chats"],
  ["✓", "Permission to share anonymized results"],
];

const Breadcrumbs = () => (
  <nav aria-label="Breadcrumb" data-testid="founding-breadcrumbs" className="fade-up mb-4 flex justify-center">
    <ol className="flex items-center gap-1 text-[13px] text-muted-foreground">
      <li><Link to="/" data-testid="breadcrumb-home" className="inline-flex min-h-[44px] items-center px-2 hover:text-foreground transition-colors">Home</Link></li>
      <li aria-hidden="true" className="text-[#E9C98B]/70">›</li>
      <li aria-current="page" className="px-2 text-[#F3DDAE]" data-testid="breadcrumb-current">Founding Members</li>
    </ol>
  </nav>
);

const CreateAccount = ({ testid }) => (
  <Button asChild className="btn-aurora h-14 rounded-2xl px-8 text-[15px]" data-testid={testid}>
    <Link to="/signup">Create my account<ArrowRight className="ml-1 h-4 w-4" /></Link>
  </Button>
);

const Hero = ({ etsyReady }) => (
    <section className="relative isolate overflow-hidden bg-[#07080F] pb-20 pt-24 sm:pt-32">
      <HeroBackdrop />
      <div className="relative mx-auto max-w-5xl px-5 text-center sm:px-8">
        <Breadcrumbs />
        <div className="fade-up mx-auto inline-flex items-center gap-2 rounded-full border border-[#E9C98B]/35 bg-[#E9C98B]/[0.07] px-4 py-1.5 text-[12px] tracking-wide text-[#F3DDAE] backdrop-blur-md">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E9C98B] shadow-[0_0_10px_#E9C98B]" />Personal invitation · Private Beta
        </div>
        <h1 data-testid="founding-h1" className="fade-up mt-6 sm:mt-8" style={{ animationDelay: "120ms" }}>
          <span data-testid="founding-seo-h1" className="block font-['Geist',ui-sans-serif,sans-serif] text-[12px] sm:text-sm font-medium uppercase tracking-[0.24em] text-[#F3DDAE]">
            Founding-member beta · Etsy SEO tool
          </span>
          <span data-testid="founding-headline" className="mt-4 block text-[2.6rem] min-[400px]:text-[3rem] leading-[0.98] sm:mt-5 sm:text-7xl lg:text-[6.6rem] lg:leading-[0.92] tracking-[-0.035em] [text-wrap:balance]">
            <span className="whitespace-nowrap">Welcome,</span> <span className="whitespace-nowrap italic text-aurora pr-1">Founding Member.</span>
          </span>
        </h1>
        <p className="fade-up mx-auto mt-7 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed" style={{ animationDelay: "240ms" }}>
          You're one of a handful of shops invited to shape AetherTags, the Etsy SEO tool and safe growth system for big Etsy catalogs. Here's how it works, what's protected, and what we ask in return.
        </p>
        <div className="fade-up mt-10 flex flex-col items-center gap-4" style={{ animationDelay: "360ms" }}>
          <CreateAccount testid="founding-create-account-button" />
          <FoundingBenefits testid="founding-hero-benefits" />
          {!etsyReady && (
            <p data-testid="founding-etsy-notice" className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.05] px-4 py-2 text-sm text-[#ECE9F5] backdrop-blur-md">
              <Info className="h-4 w-4 text-[#5EE6D0]" />Shop connection opens soon — explore Demo Mode meanwhile
            </p>
          )}
        </div>
      </div>
    </section>
);

const Steps = () => (
  <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
    <Reveal className="max-w-2xl">
      <p className="eyebrow">Four steps</p>
      <h2 className="mt-4 text-4xl sm:text-6xl leading-[1]">From Etsy shop audit <span className="italic text-gold-grad">to first fix.</span></h2>
    </Reveal>
    <ol className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="founding-steps">
      {STEPS.map(([title, text], i) => (
        <Reveal as="li" key={title} delay={i * 110} data-testid={`founding-step-${i + 1}`} className="glass hairline lift rounded-3xl border p-7">
          <span className="font-display text-5xl italic text-gold-grad">{i + 1}</span>
          <h3 className="mt-6 text-2xl">{title}</h3>
          <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{text}</p>
        </Reveal>
      ))}
    </ol>
  </section>
);

const Trust = () => (
  <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
    <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
      <Reveal>
        <p className="eyebrow">Your shop stays yours</p>
        <h2 className="mt-4 text-4xl sm:text-5xl leading-[1.02]">Safety isn't a feature. <span className="italic">It's the product.</span></h2>
        <div className="hairline relative mt-10 hidden overflow-hidden rounded-[28px] lg:block">
          <img src="/images/still-life.webp" alt="Handmade, vintage and print-on-demand Etsy products in dramatic light" loading="lazy" decoding="async" width="1264" height="848" className="aspect-[4/3] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07080F]/70 to-transparent" />
        </div>
      </Reveal>
      <ul className="grid gap-3 sm:grid-cols-2 lg:self-center" data-testid="founding-trust-points">
        {TRUST.map(([Icon, label], i) => (
          <Reveal as="li" key={label} delay={i * 70} className="glass flex items-center gap-4 rounded-2xl border p-5">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-white/10 bg-[#0E1020] shadow-[0_0_24px_-8px_#5EE6D0]">
              <Icon className="h-[18px] w-[18px] text-[#5EE6D0]" />
            </span>
            <span className="text-[15px]">{label}</span>
          </Reveal>
        ))}
        </ul>
      </div>
      <Reveal data-testid="founding-mid-cta" className="mt-12 flex flex-col items-center gap-4 text-center">
        <CreateAccount testid="founding-create-account-mid-button" />
        <FoundingBenefits testid="founding-mid-benefits" />
      </Reveal>
    </section>
);

const InReturn = () => (
  <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
    <Reveal className="hairline relative overflow-hidden rounded-[32px] p-8 sm:p-12">
      <img src="/images/glass.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" width="1264" height="848" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-[#07080F]/55" />
      <div className="relative grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
        <div>
          <p className="eyebrow">In return, we ask</p>
          <h2 className="mt-4 text-4xl sm:text-5xl leading-[1.02]">A little of your <span className="italic text-aurora">honest eye.</span></h2>
        </div>
        <ul className="grid gap-3 sm:grid-cols-3" data-testid="founding-in-return">
          {RETURN.map(([n, label]) => (
            <li key={label} className="glass rounded-2xl border p-6">
              <span className="font-display text-4xl text-gold">{n}</span>
              <p className="mt-3 text-[15px] leading-snug">{label}</p>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  </section>
);

const Coming = () => (
  <section data-testid="founding-whats-coming" className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
    <Reveal className="max-w-2xl">
      <p className="eyebrow">What's coming</p>
      <h2 className="mt-4 text-4xl sm:text-5xl leading-[1.02]">You'll shape <span className="italic text-aurora">what we build next.</span></h2>
    </Reveal>
    <div className="mt-10"><Pipeline compact tail={<ShapedTile />} /></div>
    <div className="mt-10"><FoundingPriceNote testid="founding-price-note-founding" /></div>
  </section>
);

const Faq = () => (
  <section className="mx-auto max-w-3xl px-5 pb-20 sm:px-8">
    <Reveal>
      <p className="eyebrow text-center">Questions</p>
      <h2 className="mt-4 text-center text-4xl sm:text-5xl leading-[1.02]">Before you say yes.</h2>
    </Reveal>
    <Reveal delay={120}>
      <Accordion type="single" collapsible className="glass mt-10 rounded-3xl border px-6" data-testid="founding-faq">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="border-white/[0.07] last:border-b-0">
            <AccordionTrigger data-testid={`founding-faq-${i}`} className="py-5 text-left font-display text-[1.35rem] tracking-normal hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Reveal>
    <div className="mt-14 flex flex-col items-center gap-4 text-center">
      <CreateAccount testid="founding-create-account-bottom-button" />
      <FoundingBenefits testid="founding-bottom-benefits" />
    </div>
  </section>
);

export default function Founding() {
  const { config } = useAuth();
  return (
    <PublicLayout overlay>
      <Hero etsyReady={!!config?.etsy_configured} />
      <Steps />
      <div className="gold-divider mb-24" />
      <Trust />
      <InReturn />
      <Coming />
      <div className="gold-divider mb-20" />
      <Faq />
    </PublicLayout>
  );
}
